import React from 'react';
import styled from 'styled-components';
import {Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';

const tokens = tokensForThemeProvider.sky.sys;
const compTokens = tokensForThemeProvider.sky.comp;

/** Same informational-alert shell as `AIBanner` (12px left accent bar, `comp.alerts.color...`
 * tokens, asymmetric padding) — no button here, so it is a single-line wrapper rather than the
 * `space-between` two-column layout `AIBanner` uses. */
const BannerWrapper = styled.div`
	display: flex;
	align-items: center;
	padding: ${tokens.spacing['200']} ${tokens.spacing['400']} ${tokens.spacing['200']} ${tokens.spacing['600']};
	background: ${compTokens.alerts.color.background.information};
	border-left: 12px solid ${compTokens.alerts.color.bar.information};
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(35, 35, 52, 0.12);
`;

/** Warns the editor when what is on screen may no longer match what is stored. Recovery is
 * deliberately a page reload and not a control in here: only a fresh load is guaranteed to show what
 * is stored, and this banner stays text-only.
 *
 * It used to also narrate reader visibility — whether an open draft isolated readers, or whether
 * their changes went live on save. All of that copy is gone: none of it appears in the confirmed
 * design, and the `Unsaved Changes` pill in `ReportPageHeader` covers the one thing an editor acts
 * on. So edit mode renders nothing here unless a divergence is detected. View mode is the same,
 * except that a blocking editor having just finished (`storedReportMayHaveChanged`) is worth saying
 * to an editor; the publish date lives as an inline caption beside the report name instead
 * (`ReportPageHeader`).
 *
 * Copy is resolved to plain strings via `useIntl()` — this component family's DS `Text` treats
 * children as text content and drops nested elements, so rich content must be resolved before being
 * passed in rather than nested as `FormattedMessage`. */
export function PublishStateBanner(): JSX.Element | null {
	const {mode, role, reloadRequired, definitionMayBeStale, storedReportMayHaveChanged} = useReportBuilder();
	const {formatMessage} = useIntl();
	const infoColor = compTokens.alerts.color.text.information;

	// Takes precedence over every state below, in either mode. Once what is on screen is out of step with
	// what is stored, no statement about readers holds: the published-version promise would be describing a
	// draft that has been dropped, the live-changes copy would be describing changes that were never saved,
	// and the publish date would be dating a version that may not be the one stored. So the banner states
	// the divergence and asks for a page reload instead of inviting a publish the bar has disabled anyway.
	if (reloadRequired) {
		return (
			<BannerWrapper data-cy={'ai-report-publish-state-banner'}>
				<Text color={infoColor}>{formatMessage({id: 'ai_report_builder.definition_out_of_step'})}</Text>
			</BannerWrapper>
		);
	}

	// Same reasoning as `reloadRequired` — no statement about readers holds once this tab's view may not
	// match what is stored — but this one names the cause: another user holds the lock now. The copy says
	// publishing is paused because it is: this tab's lock token no longer names a live lock it holds, so
	// report-service refuses the write. Ordered after `reloadRequired`, which is the stronger statement.
	if (definitionMayBeStale) {
		return (
			<BannerWrapper data-cy={'ai-report-publish-state-banner'}>
				<Text color={infoColor}>{formatMessage({id: 'ai_report_builder.definition_may_be_stale'})}</Text>
			</BannerWrapper>
		);
	}

	if (mode === 'view') {
		// The publish date now renders as an inline caption beside the report name (see `ReportPageHeader`)
		// — a banner with an accent bar was too heavy for a line of provenance. What survives here is the
		// one thing that is a warning rather than a date: the stored report may have moved on while a
		// blocking editor held the lock, and nothing else on screen says so.
		//
		// Editors only, like the sibling notice in `ViewTopBar`. Every site that CLEARS this flag is reached
		// through a control a reader does not have — entering edit, saving, discarding — so a reader shown
		// this notice reloads, sees an identical report, and is shown it again on the next lock transition,
		// with no way to falsify it. A reader also has nothing to lose by acting on it late: they cannot
		// publish over what the other editor stored, which is the hazard the notice exists for.
		if (storedReportMayHaveChanged && role === 'editor') {
			return (
				<BannerWrapper data-cy={'ai-report-publish-state-banner'}>
					<Text color={infoColor}>{formatMessage({id: 'ai_report_builder.stored_report_may_have_changed'})}</Text>
				</BannerWrapper>
			);
		}
		return null;
	}

	// Edit mode says nothing about readers at all. Whether a draft isolates them, or their changes go
	// live on save, is no longer narrated here: the confirmed design carries none of that copy, and the
	// `Unsaved Changes` pill in `ReportPageHeader` covers the one thing an editor acts on. What is left
	// above is the only thing this banner still exists for — a divergence between what is on screen and
	// what is stored, which gates publishing and which nothing else on the page reports.
	return null;
}
